import _ from 'lodash';
import config from 'client/shared/config';
import { observable, action, computed } from 'mobx';

import formStore from './form-store';

const VALIDATION_MAP = {
	firstName: 'Please enter your first name.',
	lastName: 'Please enter your last name',
	email: 'Please enter a valid email address.',
	phoneNumber: 'Please enter a valid US or international phone number.',
	dob: 'Please enter a valid datre (MM/DD/YYYY).',
	password: 'Please enter a valid password.',
	confirmPassword: 'Please ensure your password matches.'
};

export default class User {
	constructor (store, data) {
		this.store = store;

		if (_.isEmpty(data)) {

			return;
		}

		this.update(data);
	}

	_id = null;

	get id () {
		return this._id;
	}

	set id (id) {
		this._id = id;
	}

	/**
	 * Observables & Computeds
	 */

	 @observable
 	_profileImageData = '';

	@computed
	get profileImageData () {
		return this._profileImageData;
	}

	set profileImageData (profileImageData) {
		this._profileImageData = profileImageData;

		if (_.isEmpty(this.id)) {

			return;
		}

		this.store.saveProfileImage(this);
	}

	@observable
	_firstName = '';

	@computed
	get firstName () {
		return this._firstName;
	}

	set firstName (firstName) {
		this._firstName = firstName;
	}

	@observable
	_lastName = '';

	@computed
	get lastName () {
		return this._lastName;
	}

	set lastName (lastName) {
		this._lastName = lastName;
	}

	@observable
	_userName = '';

	@computed
	get userName () {
		return this._userName;
	}

	set userName (userName) {
		this._userName = userName;
	}

	@observable
	_email = '';

	@computed
	get email () {
		return this._email;
	}

	set email (email) {
		this._email = email;
	}

	@observable
	_phoneNumber = '';

	@computed
	get phoneNumber () {
		return this._phoneNumber;
	}

	set phoneNumber (phoneNumber) {
		this._phoneNumber = phoneNumber;
	}

	@observable
	_profileImage = '';

	@observable
	_profileImageCacheBust = 0;

	@computed
	get profileImage () {
		return this._profileImage;
	}

	set profileImage (profileImage) {
		this._profileImage = profileImage;
	}

	@computed
	get profileImagePath () {
		if (this.isProfileImageEmpty) {

			return '';
		}

		const { paths: { profileImgBasePath } } = config;
		const raw = String(this._profileImage).trim();
		const q = raw.indexOf('?');
		const pathOnly = q >= 0 ? raw.slice(0, q) : raw;
		const serverQs = q >= 0 ? raw.slice(q + 1) : '';
		const bust = this._profileImageCacheBust;
		const qsParts = [serverQs, bust ? `cb=${bust}` : ''].filter(Boolean);
		const qs = qsParts.length ? qsParts.join('&') : '';
		return qs ? `${profileImgBasePath}/${pathOnly}?${qs}` : `${profileImgBasePath}/${pathOnly}`;
	}

	@computed
	get isProfileImageEmpty () {
		return _.isEmpty(this.profileImage);
	}

	@observable
	_dob = '';

	@computed
	get dob () {
		return this._dob;
	}

	set dob (dob) {
		this._dob = dob;
	}

	@observable
	_gender = '';

	@computed
	get gender () {
		return this._gender;
	}

	set gender (gender) {
		this._gender = gender;
	}

	@observable
	_location = '';

	@computed
	get location () {
		return this._location;
	}

	set location (location) {
		this._location = location;
	}

	@observable
	_status = '';

	@computed
	get status () {
		return this._status;
	}

	set status (status) {
		this._status = status;
	}

	@observable
	_role = ''

	@computed
	get role () {
		return this._role;
	}

	set role (role) {
		this._role = role;
	}

	@observable
	_subscribedTo = {};

	@computed
	get subscribedTo () {
		return this._subscribedTo;
	}

	set subscribedTo (subscribedTo) {
		this._subscribedTo = subscribedTo;
	}

	@observable
	_hiFived = [];

	@computed
	get hiFived () {
		return this._hiFived;
	}

	set hiFived (hiFived) {
		this._hiFived = hiFived;
	}

	@observable
	_paymentCards = [];

	@computed
	get paymentCards () {
		return this._paymentCards;
	}

	set paymentCards (paymentCards) {
		this._paymentCards = paymentCards;
	}

	@observable
	_password = '';

	@computed
	get password () {
		return this._password;
	}

	set password (password) {
		this._password = password;
	}
	@observable
	_confirmPassword = '';

	@computed
	get confirmPassword () {
		return this._confirmPassword;
	}

	set confirmPassword (confirmPassword) {
		this._confirmPassword = confirmPassword;
	}

	@computed
	get fullame () {
		const { firstName, lastName } = this;
		return `${firstName} ${lastName}`;
	}

	@computed
	get firstNamePlural () {
		const firstName = this.firstName;
		return (/s$/).test(firstName) ? `${firstName}'` : `${firstName}'s`;
	}

	@computed
	get userNameForFilePath () {
		const { userName = '' } = this;
		return userName.replace(/\s/g, '_');
	}

	@computed
	get serialized () {
		return {
			_id: this._id,
			firstName: this._firstName,
			lastName: this._lastName,
			userName: this._userName,
			email: this._email,
			phoneNumber: this._phoneNumber,
			profileImage: this._profileImage,
			profileImageData: this._profileImageData,
			gender: this._gender,
			dob: this._dob,
			location: this._location,
			status: this._status,
			role: this._role,
			subscribedTo: this._subscribedTo,
			hiFived: this._hiFived,
			password: this._password,
			confirmPassword: this._confirmPassword
		};
	}

	@computed
	get jsonString () {
		return JSON.stringify(_.omit(this.serialized, ['_id']));
	}

	@observable
	_errorMessages = [];

	@computed
	get errorMessages () {
		return this._errorMessages;
	}

	@computed
	get isValid () {
		const errorMessages = [];

		_.forOwn(VALIDATION_MAP, (key, message) => {
			if (formStore.validate(this[key], `user.${key}`) !== true) {

				errorMessages.push(message);
			}
		});

		this._errorMessages = errorMessages;
		return _.isEmpty(this._errorMessages);
	};

	/**
	 * Actions
	 */

	@action.bound
	async save () {
		await this.store.saveUser(this);
	}

	@action.bound
	async delete () {
		await this.store.delete(this);
	}

	@action.bound
	update ({
		_id,
		firstName,
		lastName,
		userName,
		email,
		phoneNumber,
		profileImage,
		dob,
		gender,
		location,
		status,
		role,
		subscribedTo,
		hiFived,
		paymentCards
	}) {

		this._id = _.defaultTo(_id, this.id);
		this._firstName = _.defaultTo(firstName, this.firstName);
		this._lastName = _.defaultTo(lastName, this.lastName);
		this._userName = _.defaultTo(userName, this.userName);
		this._email = _.defaultTo(email, this.email);
		this._phoneNumber = _.defaultTo(phoneNumber, this.phoneNumber);
		if (profileImage !== undefined) {

			this._profileImageCacheBust = 0;
		}
		this._profileImage = _.defaultTo(profileImage, this.profileImage);
		this._dob = _.defaultTo(dob, this.dob);
		this._gender = _.defaultTo(gender, this.gender);
		this._location = _.defaultTo(location, this.location);
		this._status = _.defaultTo(status, this.status);
		this._role = _.defaultTo(role, this.role);
		this._subscribedTo = _.defaultTo(subscribedTo, this.subscribedTo);
		this._hiFived = _.defaultTo(hiFived, this.hiFived);
		this._paymentCards = _.defaultTo(paymentCards, this.paymentCards);
	}

	/**
	 * Methods
	 */

	getSubscription (artistID) {
		return _.find(this.subscribedTo, s => s.artistID === artistID && s.active !== false);
	}

	isHiFived (itemID) {
		return _.isEmpty(_.filter(this.hiFived, { itemID })) !== true;
	}

	async getCreditCardList () {
		try {

			const silent = true;
			const returnData = true;
			const route = config.getRoute('getCreditCardList').replace(/\:memberID$/, this.id);

			return await this.store.fetch({ route, returnData, silent });
		} catch (err) {

			console.error('[ERROR: UserStore.checkEmail]', err);
		}
	}
}
